import { useMemo } from 'react';
import { Globe, Server } from 'lucide-react';
import {
  DataTable,
  StatusPill,
  Pagination,
  EmptyState,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Badge, Button, Checkbox, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import type {
  CandidateResource,
  CandidateCounts,
  AutoAssignResult,
} from '@/api/projects';
import {
  AssignmentPill,
  candidateRowId,
  resourceStatusTone,
  type RowSelection,
} from './parts';

interface PreviewStepProps {
  items: CandidateResource[];
  counts: CandidateCounts;
  total: number;
  page: number;
  perPage: number;
  isLoading: boolean;
  isError: boolean;
  /** Controlled selection map keyed by `candidateRowId`. */
  rowSelection: RowSelection;
  onRowSelectionChange: (next: RowSelection) => void;
  /** "Select all matching" toggle (commit-by-filter). */
  selectAllMatching: boolean;
  onSelectAllMatchingChange: (next: boolean) => void;
  onPageChange: (page: number) => void;
  /** Number that will be committed (matched total when select-all, else checked count). */
  selectedCount: number;
  onDryRun: () => void;
  dryRunPending: boolean;
  dryRunResult: AutoAssignResult | null;
}

export function PreviewStep({
  items,
  counts,
  total,
  page,
  perPage,
  isLoading,
  isError,
  rowSelection,
  onRowSelectionChange,
  selectAllMatching,
  onSelectAllMatchingChange,
  onPageChange,
  selectedCount,
  onDryRun,
  dryRunPending,
  dryRunResult,
}: PreviewStepProps) {
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  const columns = useMemo<ColumnDef<CandidateResource, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Resource',
        accessorKey: 'resource_name',
        cell: ({ row }) => {
          const Icon = row.original.resource_type === 'domain' ? Globe : Server;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
              <Icon size={14} color="var(--vn-fg-muted)" />
              {row.original.resource_name}
            </span>
          );
        },
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'resource_type',
        cell: ({ getValue }) => (
          <Badge tone="neutral" appearance="outline" size="sm">
            {getValue() as string}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={resourceStatusTone(row.original.resource_status)}>
            {row.original.resource_status}
          </StatusPill>
        ),
      },
      {
        id: 'assignment',
        header: 'Assignment',
        enableSorting: false,
        cell: ({ row }) => (
          <AssignmentPill
            assignment={row.original.assignment}
            projectName={row.original.assigned_project_name}
          />
        ),
      },
    ],
    [],
  );

  if (isError) {
    return (
      <Alert tone="danger" title="Could not load candidates">
        Something went wrong while discovering resources. Adjust your conditions and try again.
      </Alert>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Badge tone="neutral" appearance="subtle" size="sm">
            {total} matching
          </Badge>
          <Badge tone="neutral" appearance="subtle" size="sm">
            Unassigned {counts.unassigned}
          </Badge>
          <Badge tone="warning" appearance="subtle" size="sm">
            In other project {counts.other_project}
          </Badge>
          <Badge tone="success" appearance="subtle" size="sm">
            In this project {counts.this_project}
          </Badge>
          <span style={{ fontSize: 13, color: 'var(--vn-fg-muted)', fontWeight: 500 }}>
            · Selected {selectedCount}
          </span>
        </div>

        <Button variant="secondary" size="sm" onClick={onDryRun} disabled={dryRunPending}>
          {dryRunPending ? 'Checking…' : 'Dry run'}
        </Button>
      </div>

      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        <Checkbox
          checked={selectAllMatching}
          onCheckedChange={(v) => onSelectAllMatchingChange(v === true)}
          aria-label="Select all matching resources beyond this page"
        />
        Select all matching ({total}) — including resources beyond this page
      </label>

      {dryRunResult ? (
        <Alert
          tone={dryRunResult.conflicts.length > 0 ? 'warning' : 'info'}
          title="Dry run preview"
        >
          {dryRunResult.matched} matched · {dryRunResult.assigned} would be assigned ·{' '}
          {dryRunResult.skipped_already_in_project} already in this project ·{' '}
          {dryRunResult.conflicts.length} also in other projects (still assigned).
        </Alert>
      ) : null}

      {isLoading ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 40 }} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={items}
          getRowId={candidateRowId}
          enableSelection={!selectAllMatching}
          rowSelection={rowSelection}
          onRowSelectionChange={(updater) =>
            onRowSelectionChange(
              typeof updater === 'function'
                ? (updater(rowSelection) as RowSelection)
                : (updater as RowSelection),
            )
          }
          emptyState={
            <EmptyState
              title="No matching resources"
              description="No resources match your conditions. Go back and adjust the filters."
            />
          }
        />
      )}

      {total > perPage ? (
        <Pagination
          page={page}
          pageCount={pageCount}
          onPageChange={onPageChange}
          summary={`Showing ${rangeStart}–${rangeEnd} of ${total}`}
        />
      ) : null}
    </div>
  );
}
